import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  effectiveConvStyle,
  type ConversationSeed,
  type CorrectionLevel,
  type GameActionRequest,
  type LessonCompletion,
} from '@orborix/shared-types';
import { useLocation } from 'react-router-dom';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import {
  readFreshReattachSession,
  useTtsVolume,
  useVoiceSession,
  type MicIssue,
} from '@orborix/voice-client';
import {
  useFreeChat,
  useHoldTurn,
  type FreeChat,
  type HoldRecorder,
} from '@orborix/voice-composer';
import { sfx } from '../../lib/sfx/index.js';
import { useListen } from '../listen/ListenProvider.js';
import { recordA0BossResult } from '../../lib/a0Api.js';
import type { ConvError } from './errorModel.js';
import type { UseCoachPickerReturn } from './hooks/useCoachPicker.js';
import type { SecretWord } from './controls/secretWords.js';
import { useCoachPicker } from './hooks/useCoachPicker.js';
import { useInspire, type UseInspireReturn } from './hooks/useInspire.js';
import { useStartContext } from './hooks/useStartContext.js';
import { useConversationSettings } from './hooks/useConversationSettings.js';
import {
  useSessionLifecycle,
  type HistorySkillCredits,
  type MicRetryPhase,
} from './hooks/useSessionLifecycle.js';
import {
  countGateTurns,
  countGateUserTurns,
  DEBRIEF_MIN_TURNS,
  isSameRowHistory,
} from './debriefGate.js';
import { useIncognito } from './hooks/useIncognito.js';
import { useObjectiveTrackers, type ActiveSubUnitClient } from './hooks/useObjectiveTrackers.js';
import { useA0Suggestions, type A0SuggestionStep } from './hooks/useA0Suggestions.js';
import { useWebSearchIndicator } from './hooks/useWebSearchIndicator.js';
import { useSeedMenu } from './hooks/useSeedMenu.js';
import { useHistoryBackGuard } from './hooks/useHistoryBackGuard.js';
import {
  useServerSessionEnded,
  useSessionBlocked,
  useSessionClosing,
  OUT_OF_CREDITS_REASON,
  type SessionClosingState,
} from './hooks/useServerSessionEnded.js';
import { buildStartModuleHints } from './startModuleHints.js';
import { speakPlayerFactory } from '../../lib/silentPlayer.js';
import { useReplyFailure, type ReplyFailureKind } from './hooks/useReplyFailure.js';
import { nativeVoiceUrl } from './nativeVoiceUrl.js';
import { attachThreadGestures, trackScrollInput, type ScrollInputTracker } from './threadGestures.js';
import { useGameArena, type GameArena } from './hooks/useGameArena.js';
import { useVoiceSessionAnalytics } from './hooks/useVoiceSessionAnalytics.js';
import type { GamePracticeHost } from './gamePracticeItems.js';
import { useProgramIntro, type ProgramIntro } from './programIntro.js';
import { nextLessonTarget, type ScenarioEndActions } from './LessonCompletedCard.js';
import { useAssistedTurn, type UseAssistedTurnReturn } from './assisted/useAssistedTurn.js';
import {
  assistedObjectives,
  soloRestartState,
  type SceneRestartState,
} from './assisted/assistedModel.js';
import { assistedMicPaused, useAssistedMicPause } from './assisted/useAssistedMicPause.js';
import type { ScenarioMission } from '../scenarios/scenarioTypes.js';
import {
  endGesture,
  INITIAL_PIN_STATE,
  markAutoScrolled,
  maxScrollTop,
  reducePin,
  releaseByGesture,
  type ThreadPinState,
} from './threadPin.js';
import {
  clamp,
  coachMutedFromMode,
  modeForCoachMuted,
  modeToSessionIo,
  SPEED_MAX,
  SPEED_MIN,
  type ConvSettings,
} from './conversationTypes.js';
import { useOtaApplyHold } from '../../lib/native/useOtaApplyHold.js';

type VoiceSession = ReturnType<typeof useVoiceSession>;

/** Respiro tras completar el boss antes de ir a la graduación (deja oír el cierre del coach). */
const BOSS_COMPLETE_NAV_DELAY_MS = 1800;

// El estado "enganchado al fondo" (y por qué no alcanza con un umbral de
// distancia) vive en `threadPin.ts`; la lectura del gesto, en `threadGestures.ts`.

/**
 * Controlador de la pantalla de conversación: encapsula TODO el estado y la
 * lógica (sesión de voz, settings ↔ sesión, grabación, inspire, drill, scroll,
 * pulso del orbe). Lo consumen las dos shells presentacionales
 * (`ConversationMobile` / `ConversationDesktop`) y la lista compartida
 * (`ConversationMessages`), de modo que la lógica vive UNA sola vez.
 *
 * La lógica está partida por feature en `hooks/` (start context, settings,
 * lifecycle, trackers de objetivos, seed menu, incógnito, web search); este
 * archivo compone y deriva los flags de render.
 */
export interface UseConversationController {
  lessonCompletion: LessonCompletion | null;
  chooseLesson: (choice: 'next' | 'chat') => void;
  leaveToProgram: () => void;
  /** Las tres salidas de un escenario terminado (tarjeta y recuadro del bonus). */
  scenarioEndActions: ScenarioEndActions;
  // Sesión + estado derivado
  session: VoiceSession;
  state: VoiceSession['state'];
  connectionStatus: VoiceSession['connectionStatus'];
  reconnect: () => void;
  messages: VoiceSession['messages'];
  transcript: string;
  assistantText: string;
  sessionId: string | null;
  /**
   * The seeded history's skill credits (its session summary), for the chips
   * of the historical bubbles (`selectThreadSkillChips`). `null` = none.
   */
  historySkillCredits: HistorySkillCredits | null;
  /** Sesión grabando audio crudo (RECORD_SESSION_AUDIO) → gatea el play del audio real. */
  audioRecording: boolean;
  paused: boolean;

  // Settings (estado de UI sembrado de prefs persistidas)
  settings: ConvSettings;
  setSettings: Dispatch<SetStateAction<ConvSettings>>;
  setGlobalIntensity: (intensity: ConvSettings['intensity']) => void;
  /** Sensibilidad de correcciones EFECTIVA (auto→CEFR / manual). Filtra el display. */
  correctionLevelEff: CorrectionLevel;
  /** Sensibilidad recomendada por CEFR (server-resolved) — guía para el SettingsSheet. */
  correctionRec: CorrectionLevel;
  /** Velocidad recomendada por nivel EFECTIVO (server-resolved) — valor del modo auto. */
  speedRec: number;
  /** Velocidad EFECTIVA (pref explícita ?? recomendada por nivel) — la que se muestra. */
  speedEff: number;
  isVoiceInput: boolean;
  isVoiceOutput: boolean;
  /** Voz del coach silenciada (toggle del header): responde SÓLO por texto
   *  (sin TTS → sin costo ElevenLabs). Persistido en prefs como `mode` vt/vv. */
  coachMuted: boolean;
  /** Alterna la voz del coach EN VIVO (aplica al output de la sesión por el
   *  diffing de settings → `mode.switch`) y persiste la pref. */
  toggleCoachMuted: () => void;
  /** Toast efímero del mute (5s con fade): visible al silenciar y al abrir
   *  una charla ya muteada; después se va solo — no ensucia el hilo. */
  muteNotice: boolean;
  /** Commit en vuelo (tap de enviar → transcript confirmado): la burbuja viva
   *  muestra "Enviando…" para que el tap tenga feedback inmediato. El aviso
   *  "no se escuchó" lo muestra el VoiceComposer solo (recorder.sendLost). */
  commitPending: boolean;
  /** Por índice de mensaje: `true` si esa respuesta del coach SONÓ (tuvo
   *  audio real). Gobierna el play por-burbuja con independencia del estado
   *  actual del mute. Índices de user/tool quedan en `false`. */
  botAudioFlags: readonly boolean[];
  /** Sin mic usable (permiso denegado o ningún dispositivo) o la sesión no
   *  abrió: el composer oculta el botón de hold y queda sólo el campo de texto.
   *  Ya NO depende de prefs — el modo sólo-texto (`tt`) murió como opción de UI
   *  y el composer es siempre dual. */
  micHidden: boolean;

  // Composer compartido (@orborix/voice-composer): push-to-talk + modo charla
  /** Hold en curso (capturando audio). */
  recording: boolean;
  recorder: HoldRecorder;
  /** Modo charla a manos libres (mic abierto entre turnos, cierre por VAD). */
  freeChat: FreeChat;
  /** Estado del pipeline para la barra viva del modo charla. */
  freeStatus: 'listening' | 'thinking' | 'speaking';
  /** Turnos del alumno confirmados por el server: sube con cada transcript final. */
  userTurnTick: number;
  /** Gate del hold (cierre server-side / apertura coach-first / turno en
   *  vuelo). El TEXTO nunca se bloquea — semántica histórica de speak. */
  holdDisabled: boolean;

  // Features
  coach: UseCoachPickerReturn;
  inspire: UseInspireReturn;
  /**
   * Charla asistida (encargo 99): la respuesta modelo para decir en cada turno,
   * en escenarios empezados en «Asistida». `phase: 'off'` = charla de siempre.
   */
  assisted: UseAssistedTurnReturn;
  /** La oferta de hacerlo solo está a la vista: la charla espera la decisión. */
  assistedPaused: boolean;

  // Envíos de texto (el campo vive dentro del composer)
  sendComposerText: (text: string) => void;
  sendInspireSuggestion: (en: string) => void;
  interrupt: () => void;

  // Charla guiada A0 (plan 33) — presente sólo si se entró con una misión A0.
  /** Misión A0 activa (null fuera de charla a0). `isBoss` = examen-gate de graduación. */
  a0Mission: { missionId: string; isBoss: boolean } | null;
  /** Paso de sugerencias VIGENTE de la red §1.1 (null = sin chips ahora). */
  a0SuggestionStep: A0SuggestionStep | null;
  /** Envía una sugerencia tap-or-speak como turno (no-corregible). */
  sendA0Suggestion: (en: string) => void;
  /** Tick que sube al completar TODOS los objetivos de una micro-charla A0 (no-boss)
   *  → dispara el festejo chico de cierre. El boss tiene su flujo aparte (graduación). */
  a0CelebrateTick: number;

  // Drill + settings sheet
  drillErr: ConvError | null;
  setDrillErr: Dispatch<SetStateAction<ConvError | null>>;
  settingsOpen: boolean;
  setSettingsOpen: Dispatch<SetStateAction<boolean>>;

  // Charla privada (incógnito) — estado EFÍMERO por sesión (no se persiste).
  incognito: boolean;
  toggleIncognito: () => void;

  // Escenario (roleplay con misión) — presente sólo si se entró desde el briefing
  /** Misión visible en el tracker en vivo (sólo curados con objetivos). */
  scenarioMission: ScenarioMission | null;
  /** Ids de objetivos cumplidos (optimista: B6 los emite por turno sobre el WS). */
  completedObjectives: ReadonlyArray<string>;
  /** Último objetivo marcado → dispara la celebración del tracker. */
  justObjectiveId: string | null;
  /** Charla asistida: objetivos practicados con ayuda (se ven aparte, no cumplen). */
  practicedObjectives: ReadonlyArray<string>;
  justPracticedId: string | null;
  /** Todos los objetivos primarios cumplidos → dispara la celebración de cierre. */
  allObjectivesDone: boolean;
  /** Primarios cumplidos y bonus pendiente: su etiqueta (recuadro del bonus). */
  bonusPending: string | null;
  /** Bonus del escenario cumplido → su festejo propio. */
  bonusDone: boolean;
  /** Título/emoji del escenario para el header de la escena. */
  scenarioLabel: { title: string; emoji: string } | null;
  /** Unidad activa del Programa (modo plan) para el header — distingue de charla libre. */
  planLabel: { title: string } | null;
  /** Objetivos outcome-framed de la unit activa (tracker en vivo, fase 16.B). */
  planObjectives: readonly string[];
  /** Índices de objetivos de unit demostrados en vivo (optimista). */
  completedPlanObjectives: readonly number[];
  /** Índice recién demostrado este turno → dispara flash/toast del tracker. */
  justPlanObjectiveIdx: number | null;
  /** Todos los objetivos de la unit demostrados en vivo → ilumina el FAB de cierre. */
  allPlanObjectivesDone: boolean;

  // Programa con sub-unidades (fase 17.B) — presente sólo en units autoradas.
  /** Sub-unidad activa (null = legacy / no es programa autorado). */
  subUnit: ActiveSubUnitClient | null;
  /** Criterios (inglés) de la sub-unidad activa — objetivos del tracker. */
  subUnitObjectives: readonly string[];
  /** Reps target por objetivo (anillo). */
  subUnitReps: readonly number[];
  /** Demos acumuladas por objetivo (optimista). */
  subUnitCounts: readonly number[];
  /** Índices de objetivos completos (count ≥ reps). */
  subUnitCompleted: readonly number[];
  /** La sub-unidad activa ya estaba aprobada y se está REPASANDO (arranca en 0). */
  subUnitReview: boolean;
  /** Índice recién avanzado este turno → flash del anillo. */
  justSubObjIdx: number | null;
  /** Tick por CADA rep nueva → toast del tracker con el conteo. */
  subRepTick: number;

  // Karaoke / captura (props estables para los leaf)
  getKaraokeRenderEndIdx: () => number;
  getKaraokeCharIndex: () => number;
  pauseMainCapture: () => void;
  resumeMainCapture: () => void;

  // Refs de layout
  /**
   * Ref del contenedor del hilo. Es un ref CALLBACK, no un objeto: además de
   * guardar el nodo cablea los listeners de gesto (rueda/touch) del autoscroll
   * respetuoso, que necesitan ser pasivos y llegar antes que el scroll.
   */
  threadRef: (el: HTMLDivElement | null) => (() => void) | undefined;
  orbRef: React.RefObject<HTMLDivElement | null>;

  // Autoscroll respetuoso (ambas shells cablean el onScroll y el chip)
  /** El user tomó el control del scroll → mostrar el chip "volver al final". */
  threadUnpinned: boolean;
  /** onScroll del contenedor del hilo: recalcula el estado enganchado/no. */
  handleThreadScroll: () => void;
  /** Salta al final del hilo y re-engancha el autoscroll. */
  jumpToLatest: () => void;

  // Flags de render
  /**
   * Charla degradada a sólo-texto porque no hay mic usable. NO es un error que
   * corte: la sesión abre igual con el input en texto y el aviso explica cómo
   * recuperar la voz. `'denied'` = el user negó el permiso; `'unavailable'` =
   * no hay dispositivo / lo tiene otra app / contexto inseguro.
   */
  micIssue: MicIssue | null;
  /** En qué anda el pedido de permiso del aviso de mic (`idle`/`asking`/`failed`). */
  micRetryPhase: MicRetryPhase;
  /**
   * Pide el mic desde el gesto del user (botón del aviso). Si lo consigue,
   * la charla EN CURSO vuelve a la voz — sin salir ni volver a entrar.
   */
  requestMic: () => void;
  /** La sesión no llegó a abrir (fallo de arranque real) → card de error. */
  startError: string | null;
  showLiveCoach: boolean;
  showLiveUser: boolean;
  /** "Coach está pensando" — incluye la apertura (instantáneo al montar). */
  showThinking: boolean;
  /**
   * Tarjeta "El coach no pudo responder" (`useReplyFailure`): el server avisó
   * `reply_failed` o el "pensando" pasó el tope de 20 s. Reemplaza al
   * "pensando" y destraba el mic. `opener` = falló el saludo.
   */
  replyFailure: ReplyFailureKind | null;
  /** Reintentar de la tarjeta: re-pide la respuesta (el user no repite nada). */
  retryFailedReply: () => void;
  /**
   * Apertura coach-first en curso (sesión fresca, el coach todavía no habló).
   * Entra al `holdDisabled` del composer: al conectar, `state` salta a
   * 'listening' optimista mientras el coach prepara el saludo — el hold debe
   * arrancar bloqueado. Mismos gates que el "pensando" de apertura
   * (resume/reattach/mic error/conexión sana).
   */
  awaitingOpener: boolean;
  /**
   * Hilo vacío y MUDO — sin burbujas, sin "pensando", sin error, sin banner.
   * Cubre los huecos donde el área de mensajes quedaba negra sin feedback:
   *  - 'loading': "Continuar"/reattach todavía sembrando el transcript previo
   *    (el start() espera `historyReady`; en red lenta son segundos).
   *  - 'your-turn': reattach que sembró 0 turnos (F5 antes de que el opener
   *    persista) — el coach NO re-saluda, le toca arrancar al user.
   */
  emptyThreadHint: 'loading' | 'your-turn' | null;
  /** Línea de intro del Programa arriba del hilo (ver `programIntro.ts`); null = no va. */
  programIntro: ProgramIntro | null;
  /**
   * Gating de interacción (fuente única — ver bloque en el cuerpo del hook).
   * `isUserTurn`: turno limpio del user (coach en silencio esperando respuesta,
   * excluye la apertura) → gate del "+" de Actividades y de Inspire.
   * `turnInFlight`: mensaje del user procesándose (thinking no-apertura) →
   * entra al `holdDisabled`. El hold permite barge-in en 'speaking', por eso
   * no usa `isUserTurn`.
   */
  isUserTurn: boolean;
  turnInFlight: boolean;
  /** "Navegando la web…" — el coach está haciendo una búsqueda web nativa. */
  isSearchingWeb: boolean;

  // Examen de nivelación: true si la sesión es la calibración opt-in (la UI
  // muestra progreso + cierre claro y oculta correcciones).
  examMode: boolean;

  // Semilla de conversación (menú "¿De qué hablamos?", modo libre)
  seedMenuOpen: boolean;
  activeSeed: ConversationSeed | null;
  /** Razón por la que el menú está inerte (programa/escenario/examen), o null. */
  seedDisabledReason: string | null;
  openSeedMenu: () => void;
  closeSeedMenu: () => void;
  selectSeed: (seed: ConversationSeed) => void;
  clearSeed: () => void;
  /** Dirección "Noticias" (news-service Fase 5): el coach trae una noticia real
   *  de la DB. Re-tocar = otra noticia (re-roll). */
  selectNews: () => void;
  goToScenarios: () => void;
  /** Palabra secreta del juego `describe-it` — CLIENT-ONLY (el coach no la ve). */
  secretWord: SecretWord | null;
  /** Nueva palabra + re-envío del seed (el re-pivote abre otra ronda). */
  rerollSecretWord: () => void;

  /** La charla se abrió desde "Escuchar" (19): muestra el mini-player y al salir
   *  marca conversado + vuelve al player. */
  listenActive: boolean;

  // Cierre server-side (`session.ended`, hoy el watch idle-no-speech del
  // backend). TERMINAL para esta instancia: la fila quedó finalizada y no se
  // reconecta — el input queda inhabilitado y la salida es resume o leave.
  /** Reason del cierre (`'idle-no-speech'`, …) o null si la sesión sigue viva. */
  serverEnded: string | null;
  /** Preaviso del cierre (countdown de 30s): la card avisa "decí algo para seguir". */
  serverClosing: SessionClosingState | null;
  /** "Retomar la charla": continúa la MISMA charla (remount con `resumeSessionId`
   *  de la sesión cerrada → historial + contexto LLM, SIN opener — habla el user). */
  resumeConversation: () => void;

  /** Game mode arena (plan 47): stamps, score pill, challenge blocks, solutions, summary. */
  gameArena: GameArena;
  /** «Practicar estas N» of the game summary: whether the session is live, and its pause. */
  gamePractice: GamePracticeHost;
  /** «Armá tu nave»: el juego activo, o el de arranque mientras la sesión conecta. */
  shipSeed: ConversationSeed | null;
  /**
   * «Armá tu nave»: buen momento para preguntar «¿seguís o volvés a la nave?» — el coach terminó su
   * turno (y su audio) y el alumno no está grabando; nunca con la charla ya cerrada.
   */
  shipCanAsk: boolean;
  /** «Volver a la nave» desde la charla: la cierra como el cierre de siempre y va al Inicio. */
  leaveToShip: () => void;

  // Navegación
  leave: () => void;
  /**
   * Salida "cuidada" desde la X: si la charla es demasiado corta para dejar un
   * debrief útil (pocos turnos del user), abre un aviso antes de irse en vez de
   * mandar a Home en seco. En cualquier otro caso llama `leave` directo. Los
   * cierres NO-voluntarios (fin de misión, examen, desconexión) siguen usando
   * `leave`, no esto.
   */
  requestLeave: () => void;
  /** ¿Está abierto el aviso de "sesión muy corta, ¿seguís o salís?". */
  shortLeavePrompt: boolean;
  /** Confirma salir igual desde el aviso (cierra el aviso y ejecuta `leave`). */
  confirmLeaveShort: () => void;
  /** Descarta el aviso y sigue en la charla. */
  dismissLeaveShort: () => void;
}

export interface ConversationControllerOpts {
  /**
   * Remonta la instancia de la conversación (bump del `key` en
   * `ConversationScreen`). Con `resumeSessionId` navega antes (replace) con ese
   * id en `location.state` → el remount entra por el flujo "Continuar" (misma
   * charla); con null remonta de cero.
   */
  onResume: (resumeSessionId: string | null) => void;
  onNextLesson?: (unitId: string, subUnitId: string) => void;
  /** «Repetir» un escenario: la misma escena en una sesión nueva. */
  onRestart?: () => void;
  /** Charla asistida: «Probar solo» arranca la misma escena de nuevo, sin ayuda. */
  onRestartScene?: (state: SceneRestartState) => void;
}

export function useConversationController(
  opts: ConversationControllerOpts,
): UseConversationController {
  const navigate = useViewNavigate();
  const location = useLocation();

  // ─── Guarda del back (browser / gesto / botón físico de Android) ────────
  // El retroceso del historial entra por `onBack` → el MISMO camino que la X
  // (`requestLeave`), y toda navegación que abandona la pantalla pasa por
  // `exitHistory` (contrato en el header del hook). `requestLeave` se define
  // más abajo (depende de `leave`, que a su vez usa `exitHistory`): se lee por
  // ref, asignada después de definirlo.
  const onBackRef = useRef<() => void>(() => {});
  const { exit: exitHistory } = useHistoryBackGuard(() => onBackRef.current());

  // Contexto de arranque (resume / escenario / listen / examen / programa):
  // viaja por location.state y se congela en el primer render.
  const rawStartState: unknown = location.state;
  const start = useStartContext(rawStartState);
  // El arranque crudo, congelado como el contexto: «Probar solo» lo repite.
  const rawStartRef = useRef(rawStartState);
  const {
    resumeSessionId,
    scenario: scenarioCtx,
    scenarioMission,
    scenarioLabel,
    listenStart,
    examMode,
    overrideUnitId,
    planLabel,
    planObjectives,
    programIntroEligible,
    a0Mission,
  } = start;

  const listen = useListen();

  // Una charla en vivo toma el canal de audio (mic + voz del coach). Si había un
  // audiolibro reproduciéndose de fondo (mini-player global), lo pausamos al
  // entrar para que no se crucen los audios. La sesión de Escuchar sigue viva: el
  // mini-player de la charla la muestra (pausada) y se puede reanudar al salir.
  const pauseListen = listen.pause;
  useEffect(() => {
    pauseListen();
  }, [pauseListen]);

  // Ref estable de la sesión para los hooks de feature (se llena más abajo, en
  // este mismo render — los effects siempre la ven poblada).
  const sessionRef = useRef<VoiceSession | null>(null);

  // ─── Settings (UI state + hidratación DB + persistencia + live updates) ─
  const {
    settings,
    setSettings,
    setGlobalIntensity,
    correctionLevelEff,
    correctionRec,
    speedRec,
    speedEff,
    prefsReady,
  } = useConversationSettings({ scenarioCtx, examMode, sessionRef });

  // Incógnito "de entrada": sólo sería true si se entrara ya en privado (no hay
  // UX para eso hoy); el caso real es el toggle mid-sesión (useIncognito).
  const incognitoAtStartRef = useRef(false);

  // Composer compartido: SIEMPRE dual (campo de texto + mic hold, estilo
  // WhatsApp — el modo sólo-texto `tt` murió como opción de UI). El IO de la
  // sesión sale de `modeToSessionIo` (input siempre 'voice'); el único eje del
  // usuario es el OUTPUT del coach — el toggle de voz del header (`coachMuted`,
  // persistido en prefs como `mode` vv/vt sin tocar el backend). El input SÍ
  // puede caer a texto, pero no por preferencia: lo decide el pre-flight del
  // mic (ver `isVoiceInput` más abajo, después del lifecycle).
  const io = modeToSessionIo(settings.mode);
  const isVoiceOutput = io.output === 'voice';
  const coachMuted = !isVoiceOutput;
  const toggleCoachMuted = useCallback((): void => {
    setSettings((s) => ({ ...s, mode: modeForCoachMuted(!coachMutedFromMode(s.mode)) }));
  }, [setSettings]);

  // Aviso efímero del mute: toast de 5s con fade al ACTIVAR el silencio y al
  // abrir una charla que ya arranca muteada (la pref hidrata async: el efecto
  // dispara en la transición null/false → true, que cubre ambos casos).
  // Desmutear lo esconde al instante. Los 5s espejan la animación CSS.
  const [muteNotice, setMuteNotice] = useState(false);
  const muteNoticeTimerRef = useRef<number | null>(null);
  const prevMutedRef = useRef<boolean | null>(null);
  useEffect(() => {
    const prev = prevMutedRef.current;
    prevMutedRef.current = coachMuted;
    if (coachMuted && prev !== true) {
      setMuteNotice(true);
      if (muteNoticeTimerRef.current != null) window.clearTimeout(muteNoticeTimerRef.current);
      muteNoticeTimerRef.current = window.setTimeout(() => setMuteNotice(false), 5_000);
    } else if (!coachMuted) {
      setMuteNotice(false);
    }
  }, [coachMuted]);
  useEffect(
    () => () => {
      if (muteNoticeTimerRef.current != null) window.clearTimeout(muteNoticeTimerRef.current);
    },
    [],
  );

  // En la app nativa (Capacitor) el WS no es same-origin: apuntamos a la base
  // absoluta del backend con el JWT embebido como `?token=` (Fase 23, A.4). En
  // web es `null` → el cliente usa su default same-origin sin cambios.
  const nativeUrl = nativeVoiceUrl();
  const session = useVoiceSession({
    moduleSlug: 'english',
    // Push-to-talk (composer compartido): el server no auto-commitea por
    // silencio; el turno cierra con el `commitTurn()` del hold (o sendText).
    // El modo charla re-negocia a 'free' en vivo vía `useFreeChat`.
    captureMode: 'hold',
    initialIoMode: io,
    // Tri-state (plan 38 F1): sólo mandamos `?speechRate=` con elección
    // explícita del user; con la pref en auto (null) lo omitimos y el server
    // resuelve la velocidad por nivel efectivo en el handshake.
    ...(settings.speed != null
      ? { initialSpeechRate: clamp(settings.speed, SPEED_MIN, SPEED_MAX) }
      : {}),
    initialTranslation: { enabled: settings.translation, targetLang: 'es' },
    // El de siempre, salvo con el audio del sistema trabado (ver `silentPlayer.ts`).
    playerFactory: speakPlayerFactory,
    moduleHints: buildStartModuleHints(start, settings, incognitoAtStartRef.current),
    ...(nativeUrl ? { url: nativeUrl } : {}),
  });
  sessionRef.current = session;

  const {
    state,
    connectionStatus,
    transcript,
    assistantText,
    messages,
    sessionId,
    audioRecording,
    conversationMode,
    commitPending,
  } = session;
  const reconnect = useCallback((): void => session.reconnect(), [session]);

  // El aviso "no se escuchó tu voz" vive en el VoiceComposer compartido
  // (recorder.sendLost + labels.nothingHeard) — acá ya no se deriva nada.

  // Estado del turno de voz → motor de SFX: gatea los sonidos `live` (objetivos,
  // palabra secreta) en modo realtime con turno activo, para no disparar un
  // barge-in falso del server-VAD.
  useEffect(() => {
    sfx.setVoiceContext(state !== 'idle', conversationMode === 'realtime');
    // Reset al desmontar: el singleton sobrevive a la pantalla, y un
    // `voiceActive` viejo gatearía sonidos `live` fuera de la conversación.
    return () => sfx.setVoiceContext(false, false);
  }, [state, conversationMode]);

  // Aviso sonoro (calmo) cuando la conexión de voz se pierde. UNA sola vez por
  // corte: desde `lost` el hook sigue auto-reintentando (ver `04-voice-pipeline`
  // § Reconexión automática), así que cada ciclo fallido vuelve a pasar por
  // `lost` — sin este gate el user escucharía el aviso cada ~30s. Se rearma
  // cuando la conexión efectivamente vuelve.
  const lostSfxPlayedRef = useRef(false);
  useEffect(() => {
    if (connectionStatus === 'open') lostSfxPlayedRef.current = false;
    if (connectionStatus !== 'lost' || lostSfxPlayedRef.current) return;
    lostSfxPlayedRef.current = true;
    sfx.play('connection-lost');
  }, [connectionStatus]);

  // PWA auto-update (main.tsx): marca si hay sesión de voz EN VIVO para que el
  // auto-reload del Service Worker no corte la conversación. Al volver a 'idle'
  // emite `voice-session-ended` → si quedó una recarga diferida, se aplica ahí.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const active = connectionStatus !== 'idle';
    window.__voiceSessionActive = active;
    if (!active) window.dispatchEvent(new Event('voice-session-ended'));
  }, [connectionStatus]);
  // Native OTA (lib/native/ota.ts): the same guard for the app's bundle updates. A
  // reload kills the session; from connecting to closed (reconnects included) no
  // downloaded bundle is applied, not even when the app goes to the background.
  useOtaApplyHold('voice-session', connectionStatus !== 'idle');

  // Product analytics (lib/analytics.ts): session start / end with mode, duration and
  // the learner's turn count — never what was said.
  const userTurns = useMemo(() => messages.filter((m) => m.who === 'user').length, [messages]);
  useVoiceSessionAnalytics({ connectionStatus, start, userTurns });

  // Getters estables para pasar como props (evita `unbound-method`).
  const getOrbEnergy = useCallback((): number => session.getOrbEnergy(), [session]);
  const getKaraokeCharIndex = useCallback((): number => session.getKaraokeCharIndex(), [session]);
  const getKaraokeRenderEndIdx = useCallback(
    (): number => session.getKaraokeRenderEndIdx(),
    [session],
  );
  const pauseMainCapture = useCallback((): void => session.pauseCapture(), [session]);
  const resumeMainCapture = useCallback((): void => session.resumeCapture(), [session]);
  const interrupt = useCallback((): void => void session.interrupt(), [session]);

  // ─── Feature hooks ─────────────────────────────────────────────────────
  // Composer compartido: hold push-to-talk + modo charla. Mientras el modo
  // charla está activo, el recorder va `suspended` (su guard de "mic cerrado
  // entre turnos" pausaría la captura en cada transición y mataría el modo).
  const freeChat = useFreeChat(session);
  const recorder = useHoldTurn(session, { suspended: freeChat.active });
  const recording = recorder.recording;
  const freeActiveRef = useRef(false);
  freeActiveRef.current = freeChat.active;
  // Con el tono EFECTIVO (warm en starter/cozy): la demo de voz (Gemini) suena
  // como va a sonar la charla.
  const coach = useCoachPicker(session, {
    tone: effectiveConvStyle(settings).tone,
    region: settings.region,
  });
  const inspire = useInspire(messages, assistantText, sessionId);

  // Reattach tras F5/crash: leemos UNA vez (al montar) el sessionId persistido
  // de una charla que se cortó por recarga. El hook reattachea solo al WS; acá
  // sólo lo usamos para sembrar el historial visible (el reload perdió el
  // `location.state`, así que `resumeSessionId` no aplica). Se lee antes del
  // primer `start()`, mientras el id sigue fresco.
  const [reattachSessionId] = useState<string | null>(
    () => readFreshReattachSession('english') ?? null,
  );

  // Charla asistida: sólo escenarios curados con misión, empezados en
  // «Asistida» y no retomados (una charla retomada sigue como charla común).
  const assistedActive =
    scenarioCtx?.mode === 'assisted' && scenarioMission != null && !resumeSessionId;
  // Con la hoja de instrucciones de la asistida abierta la sesión no se abre: el
  // coach saluda cuando el alumno la cierra (Emmanuel, 19/10). Un reattach (F5) no
  // espera: retoma la charla y el coach no vuelve a saludar.
  const [assistedIntroOpen, setAssistedIntroOpen] = useState(assistedActive);
  const holdStart = assistedIntroOpen && !reattachSessionId;

  // ─── Lifecycle: start/stop + siembra del historial al retomar ──────────
  const {
    micIssue,
    micRetryPhase,
    requestMic,
    startError,
    historyReady,
    historySkillCredits,
    historyGameSolutions,
  } =
    useSessionLifecycle({
      sessionRef,
      prefsReady,
      resumeSessionId,
      reattachSessionId,
      holdStart,
    });

  // Sin mic usable (denegado o inexistente) el hold sería un botón muerto: se
  // oculta y queda el canal de texto — la charla sigue, escrita (el lifecycle
  // abrió la sesión con el input en texto). También con la sesión sin abrir,
  // donde ningún control tiene a dónde ir. Único caso de `micHidden` hoy (antes
  // también lo activaba el modo sólo-texto `tt`, que murió).
  const micHidden = micIssue != null || startError != null;
  // El input REAL de la sesión (no la preferencia): con el mic caído el
  // lifecycle arrancó en texto, y la pantalla tiene que leerlo así — el
  // coachmark del primer turno apunta al campo en vez del mic ausente, y la
  // burbuja de transcript vivo (que no va a llegar nunca) no se arma.
  const isVoiceInput = io.input === 'voice' && !micHidden;
  // Cambio de modo I/O mid-sesión: `setIoMode` re-arma el mic al volver a un
  // modo con entrada de voz — en push-to-talk debe quedar PAUSADO hasta el
  // próximo hold (el guard de useHoldTurn recién corre en la próxima
  // transición del pipeline). En modo charla no: el mic abierto es el punto.
  useEffect(() => {
    if (isVoiceInput && !freeActiveRef.current) sessionRef.current?.pauseCapture();
  }, [isVoiceInput]);

  // ─── Cierre server-side (watch idle-no-speech del backend) ─────────────
  // El voice-client ya marcó el cierre como intencional (sin reconexión) y
  // liberó mic/player; acá le damos UI: card terminal al final del hilo +
  // controles inhabilitados. "Retomar la charla" CONTINÚA la misma charla:
  // `onResume(sessionId)` navega (replace) con `resumeSessionId` de la sesión
  // cerrada y remonta la instancia → flujo "Continuar" completo (historial
  // sembrado + contexto LLM re-hidratado, SIN opener nuevo — habla el user).
  // El `sessionId` del hook sobrevive al cierre (el cleanup server-ended no lo
  // resetea); si igual faltara, `onResume(null)` degrada a arranque de cero.
  //
  // El corte por SALDO (`session.blocked` + close 4402) entra por el mismo
  // canal terminal: el voice-client también lo marca como cierre deliberado, y
  // acá lo plegamos sobre `serverEnded` para heredar de una todo el gating que ya
  // cuelga de él (composer mudo, popovers cerrados, grabación cancelada, card
  // al final del hilo). La card distingue el reason y ofrece cargar orbs en
  // vez de un "Retomar" condenado a rebotar contra el preflight.
  const outOfCredits = useSessionBlocked({ sessionRef });
  const serverEnded = useServerSessionEnded() ?? (outOfCredits ? OUT_OF_CREDITS_REASON : null);
  const serverClosing = useSessionClosing();

  const assistedGoals = useMemo(
    () => (scenarioMission ? assistedObjectives(scenarioMission.objectives) : []),
    [scenarioMission],
  );
  const assisted = useAssistedTurn({
    active: assistedActive,
    sceneTitle: scenarioMission?.title ?? '',
    scenarioId: scenarioCtx?.scenarioId ?? null,
    objectives: assistedGoals,
    session,
    sessionId,
    messages,
    assistantText,
    recorder,
    ended: serverEnded != null,
    onRestartSolo: () => {
      if (!scenarioCtx) return;
      void sessionRef.current?.stop();
      const state = soloRestartState(rawStartRef.current, scenarioCtx);
      exitHistory(() => opts.onRestartScene?.(state));
    },
  });
  // Cerró la hoja («Empezar la escena» o «Mejor solo»): ahora sí, la sesión.
  useEffect(() => {
    if (assisted.phase !== 'intro') setAssistedIntroOpen(false);
  }, [assisted.phase]);
  // La oferta de hacerlo solo pausa la charla hasta que el alumno decide
  // (Emmanuel, 14/10): manos libres silenciado y el composer apagado. Después
  // de un intento mal, manos libres también deja de escuchar hasta «Decila una
  // vez más» (16/10).
  const assistedPaused = assisted.phase === 'offer';
  useAssistedMicPause(assistedMicPaused(assisted.phase), freeChat);
  // A diferencia del cierre por idle, ese `stop()` SÍ resetea el `sessionId`
  // del hook — sin este espejo, salir tras el corte perdía el debrief de la
  // charla que igual ocurrió.
  const lastSessionIdRef = useRef<string | null>(null);
  if (sessionId != null) lastSessionIdRef.current = sessionId;
  // Dev panel (src/dev/): announce the live session id. `__DEV_TOOLS__` is a
  // build-time constant, so the prod bundle drops the dispatch.
  useEffect(() => {
    if (!__DEV_TOOLS__ || sessionId == null) return;
    window.dispatchEvent(new CustomEvent('orborix:dev.session', { detail: { sessionId } }));
  }, [sessionId]);
  const { onResume } = opts;
  const resumeConversation = useCallback((): void => {
    // Pasa por la guarda del back: `onResume(id)` REEMPLAZA la entrada de
    // arriba (sin esto sería el centinela) y el remount re-arma la guarda limpia.
    exitHistory(() => onResume(sessionRef.current?.sessionId ?? null));
  }, [onResume, exitHistory]);

  // Keep the live TTS volume in sync with the persisted preference.
  const { volume } = useTtsVolume();
  useEffect(() => {
    // Muteado, el volumen efectivo es 0 SIEMPRE: el server ya cancela el TTS
    // remoto con el mode.switch, pero los chunks bufferizados en el cliente
    // seguían sonando — esto los calla al instante (mutear con el coach
    // hablando lo frena en seco). Desmutear restaura el volumen del user.
    session.setOutputVolume(coachMuted ? 0 : volume);
  }, [volume, session, coachMuted]);

  // Registro POR-BURBUJA de si la respuesta del coach SONÓ: el play de una
  // burbuja cerrada depende de si ESE turno tuvo audio, no del estado actual
  // del mute (que puede togglearse varias veces por sesión). `turnAudioRef`
  // marca si el turno en curso llegó a reproducir audio real; los mensajes
  // bot heredan la marca al commitearse. Un batch de 2+ mensajes nuevos =
  // seed de historial (reattach): sin datos del pasado se asume que sonaron
  // (el replay re-sintetiza, así que el play siempre funciona).
  const [botAudioFlags, setBotAudioFlags] = useState<readonly boolean[]>([]);
  const turnAudioRef = useRef(false);
  const flagsLenRef = useRef(0);
  useEffect(() => {
    if (session.isAudioPlaying) turnAudioRef.current = true;
  }, [session.isAudioPlaying]);
  useEffect(() => {
    if (messages.length <= flagsLenRef.current) return;
    const start = flagsLenRef.current;
    const isSeedBatch = messages.length - start > 1;
    const adds: boolean[] = [];
    for (let i = start; i < messages.length; i++) {
      adds.push(messages[i]?.who === 'bot' ? (isSeedBatch ? true : turnAudioRef.current) : false);
    }
    flagsLenRef.current = messages.length;
    if (!isSeedBatch) turnAudioRef.current = false;
    setBotAudioFlags((prev) => [...prev, ...adds]);
  }, [messages]);

  // ─── Semilla de conversación (menú "¿De qué hablamos?", modo libre) ─────
  const seedMenu = useSeedMenu({
    sessionRef,
    examMode,
    inProgram: overrideUnitId != null,
    inScenario: scenarioCtx != null,
    // Intensidad efectiva de la sesión: calibra el banco de palabras secretas
    // del juego `describe-it` (client-only).
    intensity: settings.intensity,
    exitScreen: exitHistory,
  });

  // Juego de arranque («Armá tu nave», pieza Motores): una vez, apenas la sesión está conectada.
  const autoSeedRef = useRef(start.autoSeed);
  const { selectSeed: selectSeedOnce } = seedMenu;
  useEffect(() => {
    const seed = autoSeedRef.current;
    if (!seed || sessionId == null) return;
    autoSeedRef.current = null;
    selectSeedOnce(seed);
  }, [sessionId, selectSeedOnce]);

  // ─── Charla privada (incógnito) ─────────────────────────────────────────
  const { incognito, toggleIncognito, incognitoRef } = useIncognito({ sessionRef, sessionId });

  // ─── Trackers de objetivos en vivo (misión / unit / sub-unidad) ─────────
  const trackers = useObjectiveTrackers({ scenarioMission, planObjectives });

  // ─── Red A0 (plan 33 §1.1): sugerencias tap-or-speak del objetivo pendiente.
  // GOTCHA: el hook se monta SIEMPRE (gateado adentro por `hasMission`) — montarlo
  // condicional por mode='a0' es el patrón "Rendered more hooks" que ya explotó.
  const a0Suggestions = useA0Suggestions({ hasMission: a0Mission != null });

  // ─── Boss-charla completada (plan 33 §1.3) → graduación. Cuando el server
  // señala `a0.mission.complete` y es el boss, marcamos `bossPassed`
  // (POST /a0/boss-result) y volvemos al motor A0 con el flag `a0BossPassed`:
  // A0Screen dispara la graduación (ahora pasa: maestría + bossPassed) → ceremonia
  // → reveal. Dejamos un respiro para que el cierre del coach se escuche. El hook
  // se monta SIEMPRE (gate adentro). Las micro-charlas (no-boss) no gradúan: la
  // red se retira sola y el alumno sale cuando quiere.
  useEffect(() => {
    if (!a0Mission?.isBoss) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onComplete = (): void => {
      // best-effort: el gate real vive server-side (graduate revalida bossPassed).
      void recordA0BossResult(a0Mission.missionId).catch(() => {});
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void sessionRef.current?.stop();
        exitHistory(() => void navigate('/home', { replace: true, state: { a0BossPassed: true } }));
      }, BOSS_COMPLETE_NAV_DELAY_MS);
    };
    window.addEventListener('orborix:a0.mission.complete', onComplete);
    return () => {
      window.removeEventListener('orborix:a0.mission.complete', onComplete);
      if (timer) clearTimeout(timer);
    };
  }, [a0Mission, navigate, exitHistory]);

  // ─── Micro-charla A0 (no-boss) completada (plan 33) → festejo chico. Cuando el
  // server señala `a0.mission.complete` y NO es el boss, subimos un tick que las
  // shells pasan a <CelebrationLayer type="sparkles" trigger={…}>. La micro-charla NO gradúa ni navega: la red de chips se
  // retira sola y el alumno sale con la X cuando quiere. El hook se monta SIEMPRE
  // (gate adentro), igual que el del boss.
  const [a0CelebrateTick, setA0CelebrateTick] = useState(0);
  useEffect(() => {
    if (!a0Mission || a0Mission.isBoss) return;
    const onComplete = (): void => setA0CelebrateTick((t) => t + 1);
    window.addEventListener('orborix:a0.mission.complete', onComplete);
    return () => window.removeEventListener('orborix:a0.mission.complete', onComplete);
  }, [a0Mission]);

  // ─── Web search nativo (indicador + fuentes) ────────────────────────────
  const { isSearchingWeb } = useWebSearchIndicator(assistantText);

  // ─── Orb energy → header pulse (rAF, no per-frame re-render) ───────────
  const orbRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let raf = 0;
    const tick = (): void => {
      if (orbRef.current) orbRef.current.style.setProperty('--orb-energy', String(getOrbEnergy()));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getOrbEnergy]);

  // ─── Inspire / envío de texto (el campo vive dentro del composer) ──────
  const sendComposerText = (text: string): void => {
    // Sesión terminada server-side: `session.sendText` sería un no-op
    // silencioso (WS cerrado) — la card terminal ya ofrece retomar/salir.
    if (serverEnded != null || trackers.lessonCompletion) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    inspire.close();
    session.sendText(trimmed);
  };
  const sendInspireSuggestion = (en: string): void => {
    if (serverEnded != null) return;
    inspire.close();
    // Fase 14C: marcamos el turno como "de Inspirar" para que el backend NO lo
    // corrija — la app no corrige su propia sugerencia.
    session.sendText(en, { fromSuggestion: true });
  };
  // Red A0 (plan 33 §1.1): tocar un chip envía la frase como turno del user.
  // OJO: NO usamos `fromSuggestion` acá (a diferencia de Inspirar). Ese flag hace
  // que el side-runner SALTEE el turno entero (skip de correcciones), y en a0 el
  // side-runner es lo ÚNICO que juzga los objetivos → con el flag el chip nunca
  // marca el objetivo y la red queda trabada (cazado por la QA visual E2E). En a0
  // las correcciones YA van OFF (suppressCorrections), así que el flag es
  // redundante: lo omitimos para que el turno SÍ se juzgue y la misión avance.
  const sendA0Suggestion = (en: string): void => {
    if (serverEnded != null) return;
    session.sendText(en);
  };

  // ─── Drill modal + settings sheet ──────────────────────────────────────
  const [drillErr, setDrillErr] = useState<ConvError | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Cierre server-side: plegar los popovers/sheets abiertos (inspire, menú de
  // actividades, ajustes) — sus acciones irían a un WS muerto. Idempotente;
  // los botones que los abren quedan inhabilitados en las shells.
  const { close: inspireClose } = inspire;
  const { closeSeedMenu: seedMenuClose } = seedMenu;
  useEffect(() => {
    if (serverEnded == null) return;
    inspireClose();
    seedMenuClose();
    setSettingsOpen(false);
    // Grabación en vuelo (hold sostenido o take fijado/pausado) al morir la
    // sesión: cancelarla — sin esto el panel fijado quedaba montado con el
    // timer corriendo contra un WS muerto.
    if (recorder.recording) recorder.cancel();
  }, [serverEnded, inspireClose, seedMenuClose, recorder]);

  // ─── Scroll-to-bottom on new content ──────────────────────────────────
  // El coach streamea token a token → este effect dispararía un read de
  // `scrollHeight` (fuerza reflow) + write de `scrollTop` en CADA token.
  // Coalescemos en un `requestAnimationFrame`: varios tokens dentro del mismo
  // frame colapsan a una sola escritura de layout, y cancelamos el rAF
  // pendiente al desmontar.
  //
  // Autoscroll RESPETUOSO: sólo seguimos el contenido nuevo si el user está
  // "enganchado" al fondo. Si scrolleó arriba (p.ej. a releer una corrección
  // mientras el coach streamea), NO lo arrastramos — las shells muestran el
  // chip "volver al final" (`threadUnpinned` + `jumpToLatest`) en su lugar.
  // El enganche se recalcula en el onScroll del contenedor
  // (`handleThreadScroll`, cableado por ambas shells) y también dentro del
  // loop de rAF, antes de cada escritura. La decisión vive en `threadPin.ts`:
  // se mide QUIÉN movió el scroll (nosotros vs. el usuario), no la distancia
  // al fondo — con un umbral de distancia el loop de abajo revertía cada
  // frame el gesto antes de que llegara a cruzarlo.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinRef = useRef<ThreadPinState>(INITIAL_PIN_STATE);
  const [threadUnpinned, setThreadUnpinned] = useState(false);
  // Charla asistida (encargo 113): la deriva del scroll suelta el hilo sólo si
  // el usuario hizo algo que lo mueva. Al empezar la línea nueva del coach, la
  // grande de antes se achica y la nueva crece en el mismo frame: el browser
  // recortaba el scroll a mitad de camino y se leía como el alumno subiendo
  // (sin tocar nada, «Volver al final» tapaba la hoja). Sólo acá: en la charla
  // de siempre un scroll programático (los coachmarks) tiene que soltarlo.
  const scrollInputRef = useRef<ScrollInputTracker | null>(null);
  const scrollInputOnlyRef = useRef(false);
  scrollInputOnlyRef.current = assisted.deckVisible;

  /** Aplica un estado nuevo del enganche y sincroniza el chip (sólo en la transición). */
  const setPinState = useCallback((next: ThreadPinState): void => {
    const was = pinRef.current.pinned;
    pinRef.current = next;
    if (was !== next.pinned) setThreadUnpinned(!next.pinned);
  }, []);

  /** Relee el DOM, recalcula el enganche y sincroniza el chip. Devuelve si seguimos pegados. */
  const syncPin = useCallback(
    (el: HTMLDivElement): boolean => {
      const input = scrollInputRef.current;
      setPinState(
        reducePin(pinRef.current, el, {
          ...(scrollInputOnlyRef.current && input ? { scrollInput: input.active() } : {}),
        }),
      );
      return pinRef.current.pinned;
    },
    [setPinState],
  );

  /**
   * Pega el hilo al fondo — pero sólo si el usuario no tomó el control.
   * Chequea el enganche contra el DOM ANTES de escribir: un gesto en curso
   * puede no haber emitido su `scroll` todavía, y escribir igual sería
   * justamente el tirón hacia abajo que estamos evitando.
   *
   * Con el hilo suelto sale sin tocar el DOM: este loop corre 60 veces por
   * segundo JUSTO mientras el usuario scrollea, y cada lectura de
   * `scrollTop`/`scrollHeight` fuerza un reflow sincrónico que compite con su
   * gesto (y con el rAF del karaoke, que muta el DOM en el mismo frame).
   */
  const followBottom = useCallback((): void => {
    const el = scrollRef.current;
    if (!el || !pinRef.current.pinned) return;
    if (!syncPin(el)) return;
    // Escribir `scrollHeight` (no el máximo calculado): el clamp del browser
    // da el fondo EXACTO, que puede ser fraccionario mientras `scrollHeight` y
    // `clientHeight` vienen redondeados. El guard evita reescribir (y ensuciar
    // el scroll) cuando ya estamos abajo.
    if (el.scrollTop < maxScrollTop(el)) el.scrollTop = el.scrollHeight;
    // Anotar el valor REAL que quedó (el browser clampea y puede ser
    // fraccionario): es contra esto que se mide el movimiento del usuario.
    pinRef.current = markAutoScrolled(pinRef.current, el.scrollTop);
  }, [syncPin]);

  const handleThreadScroll = useCallback((): void => {
    const el = scrollRef.current;
    if (el) syncPin(el);
  }, [syncPin]);

  const jumpToLatest = useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    setPinState({ pinned: true, autoTop: el.scrollTop, upDrift: 0, driving: false });
    el.scrollTop = el.scrollHeight;
    pinRef.current = markAutoScrolled(pinRef.current, el.scrollTop);
  }, [setPinState]);

  // ─── Gesto del usuario (capa 1 del enganche; ver `threadGestures.ts`) ──
  /** Cierra el gesto y reevalúa UNA vez: un empujón mínimo que terminó pegado al fondo re-engancha. */
  const finishGesture = useCallback((): void => {
    setPinState(endGesture(pinRef.current));
    const el = scrollRef.current;
    if (el) syncPin(el);
  }, [setPinState, syncPin]);

  /** Ref callback del contenedor: guarda el nodo y cablea los listeners de gesto. */
  const threadRef = useCallback(
    (el: HTMLDivElement | null): (() => void) | undefined => {
      scrollRef.current = el;
      if (!el) return undefined;
      const detach = attachThreadGestures(el, {
        onUpIntent: () => setPinState(releaseByGesture(pinRef.current)),
        onGestureEnd: finishGesture,
      });
      const input = trackScrollInput(el);
      scrollInputRef.current = input;
      // El hilo se ACHICA sin que cambie ningún dep del effect de abajo: el
      // teclado (con `interactive-widget=resizes-content` el frame entero se
      // encoge — ver viewportHeight.ts), el hint o la card que aparecen bajo
      // él. El browser conserva `scrollTop` al encoger el contenedor, así que
      // el fondo queda cortado justo cuando el user va a escribir; con el hilo
      // enganchado lo volvemos a pegar. `followBottom` no escribe si el user
      // tomó el control. (Cuando el hilo CRECE no hace falta: el clamp del
      // browser baja `scrollTop` solo y `threadPin.ts` lo absorbe.)
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(followBottom) : null;
      ro?.observe(el);
      // También el CONTENIDO (cada hijo del hilo, y los que entran): cuando una
      // burbuja se achica un frame y vuelve a crecer (el swap viva→cerrada, la
      // hoja de la asistida que cambia de fase), el browser baja `scrollTop` y el
      // evento de scroll llega con el contenido ya crecido — `reducePin` lo leía
      // como el usuario subiendo y soltaba el hilo («Volver al final» sin tocar
      // nada). El ResizeObserver corre después del layout de ESE frame, antes que
      // el evento: re-engancha con las medidas del recorte.
      const watch = (node: Node): void => {
        if (node instanceof Element) ro?.observe(node);
      };
      el.childNodes.forEach(watch);
      const mo =
        ro && typeof MutationObserver !== 'undefined'
          ? new MutationObserver((ms) =>
              ms.forEach((m) => {
                m.addedNodes.forEach(watch);
                m.removedNodes.forEach((n) => {
                  if (n instanceof Element) ro?.unobserve(n);
                });
              }),
            )
          : null;
      mo?.observe(el, { childList: true });
      return () => {
        mo?.disconnect();
        ro?.disconnect();
        detach();
        input.detach();
        scrollInputRef.current = null;
        scrollRef.current = null;
      };
    },
    [finishGesture, setPinState, followBottom],
  );
  useEffect(() => {
    // Doble snap (rAF + rAF siguiente): el primero engancha el fondo con el
    // layout del commit; el segundo re-asegura tras los ajustes que el browser
    // aplica DESPUÉS del primer frame. Caso real (2026-07-21): al commitear la
    // burbuja viva del coach, la cerrada monta con `conv-cv`
    // (content-visibility) y su primer layout puede usar el placeholder de
    // `contain-intrinsic-size` (140px) — el snap simple leía un scrollHeight
    // corto, la burbuja se expandía al frame siguiente y el hilo quedaba
    // scrolleado arriba del fondo justo al terminar la reproducción.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      followBottom();
      raf2 = requestAnimationFrame(followBottom);
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [
    messages,
    state,
    transcript,
    assistantText,
    recording,
    inspire.open,
    serverEnded,
    serverClosing,
    trackers.lessonCompletion,
    // La hoja de la charla asistida es lo último del hilo: cambia de alto con
    // su fase («Casi», la oferta) y el hilo enganchado tiene que seguirla.
    assisted.phase,
    followBottom,
  ]);

  // Drenaje del audio: entre el fin de la generación y el fin ACÚSTICO, el
  // karaoke sigue AGREGANDO palabras al DOM (SpokenBody revela tokens con su
  // propio rAF interno) sin tocar ninguno de los deps del effect de arriba —
  // el hilo crecía por debajo del fold sin que nada re-enganche el fondo.
  // Mientras suena audio y el user está pinned, este loop mantiene el fondo a
  // la vista frame a frame (escribir el mismo scrollTop es no-op: sin jitter;
  // si el user scrollea arriba, `followBottom` lo detecta ANTES de escribir y
  // corta el seguimiento — ver `threadPin.ts`).
  useEffect(() => {
    if (!session.isAudioPlaying) return;
    let raf = 0;
    const tick = (): void => {
      followBottom();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [session.isAudioPlaying, followBottom]);

  // Live coach bubble: visible siempre que haya texto del assistant sin commitear.
  const showLiveCoach = assistantText.length > 0;
  // Live user bubble. No la mostramos en la ventana de apertura (coach-first):
  // al conectar, `state` salta a 'listening' optimista y en modo libre el hook
  // de grabación prende `recording` antes de que el coach hable, produciendo un
  // bubble vacío ("Te escucho…") que aparece, desaparece cuando el coach toma
  // el turno, y reaparece después. Lo gateamos hasta que el coach haya hablado
  // al menos una vez (caso normal) o el usuario ya tenga transcript real (caso
  // raro sin opener del coach).
  // En modo teclado no hay burbuja viva: el texto se commitea entero con
  // sendText (sin transcript streaming), así que el gate por `isVoiceInput`
  // evita el "Te escucho…" fantasma al pasar a teclado mid-sesión.
  const coachHasSpoken = messages.some((m) => m.who === 'bot');
  // `commitPending` mantiene la burbuja viva entre el tap de enviar y el
  // transcript confirmado (ahí muestra "Enviando…") — incluso sin partial
  // pintado (p.ej. un "hello" corto cuyo partial se suprimió): sin esto el
  // tap quedaba sin feedback durante el commit del STT (300-1000ms).
  const showLiveUser =
    isVoiceInput &&
    (coachHasSpoken || transcript.length > 0 || commitPending) &&
    (recording || commitPending || (state === 'listening' && transcript.length > 0));

  // Indicador "Coach está pensando" en la APERTURA. Antes sólo aparecía cuando
  // el server flippeaba a `state:'thinking'` (ya generando), dejando un hueco
  // mudo entre que entrás a la conversación y eso ocurre (hidratación de prefs +
  // connect del WS + arranque del saludo en el server). Lo mostramos de entrada
  // —apenas montás— y hasta que la primera burbuja del coach empieza a
  // streamear, así el user entiende que algo está pasando mientras se gesta el
  // resto por detrás. Acotado a la apertura de una sesión fresca:
  //   - `historyReady`: en "Continuar" esperamos a sembrar el historial (ahí el
  //     coach ya habló → no es apertura).
  //   - `!coachHasSpoken`: una vez que el coach habló, cae al comportamiento
  //     mid-conversación (sólo mientras `state==='thinking'`).
  //   - conexión sana (`idle`/`connecting`/`open`): en `reconnecting`/`lost`
  //     manda el `ConnectionBanner`, no fingimos que está "pensando".
  const connectionHealthy =
    connectionStatus === 'idle' || connectionStatus === 'connecting' || connectionStatus === 'open';
  //   - `!reattachSessionId`: un reattach (F5) retoma la MISMA charla → el coach
  //     NO manda opener (no re-saluda). Sin este gate, la pantalla quedaba
  //     "pensando" para siempre esperando un opener que no llega (y el borde de
  //     un reattach con 0 turnos ni siquiera tiene historial que sembrar).
  //   - `!startError`: sin sesión abierta no hay opener en camino. Un mic caído
  //     NO entra acá: esa charla abre igual (sólo-texto) y el coach saluda.
  //   - `!holdStart`: con la hoja de instrucciones de la asistida abierta la
  //     sesión no se abrió todavía; «pensando» (y el tope de 20 s) recién al
  //     cerrarla.
  const awaitingOpenerRaw =
    !holdStart &&
    historyReady &&
    !coachHasSpoken &&
    !showLiveUser &&
    !startError &&
    connectionHealthy &&
    !reattachSessionId;
  // "El coach no pudo responder" (aviso `reply_failed` del server o tope de
  // 20 s): reemplaza al "pensando" y deja de bloquear el mic — sin esto, con el
  // saludo fallido `awaitingOpener` seguía en true para siempre (sólo cae con
  // la primera burbuja del coach) y la pantalla quedaba "pensando" con el mic
  // apagado hasta el cierre por inactividad.
  const { failure: replyFailure, rearm: rearmReplyWatchdog } = useReplyFailure({
    replyFailed: session.replyFailed,
    thinking: !showLiveCoach && (state === 'thinking' || awaitingOpenerRaw),
    coachHasSpoken,
  });
  const awaitingOpener = awaitingOpenerRaw && replyFailure == null;
  const showThinking =
    replyFailure == null && !showLiveCoach && (state === 'thinking' || awaitingOpener);
  const retryFailedReply = useCallback((): void => {
    rearmReplyWatchdog();
    // Con el socket caído no hay a quién pedirle el reintento: el banner de
    // conexión manda, y reconectar es lo único que destraba.
    if (session.connectionStatus === 'open') session.retryReply();
    else session.reconnect();
  }, [rearmReplyWatchdog, session]);

  // ── Gating de interacción (fuente única) ──────────────────────────────────
  // Antes cada control comparaba `state` crudo por su lado y la lógica drifteaba
  // entre pantallas: el pill de Actividades quedaba clickeable durante la
  // apertura y mientras el coach hablaba porque sólo miraba `state==='thinking'`
  // (y `state` salta a 'listening' optimista antes del opener). Estos flags
  // nombran el turno UNA vez; cada control referencia el que le corresponde en
  // vez de re-derivar comparaciones de `state`.
  const gateEnded =
    serverEnded != null ||
    trackers.lessonCompletion?.phase === 'closing' ||
    trackers.lessonCompletion?.phase === 'closed';
  // Turno limpio del usuario: el coach terminó y espera respuesta. Excluye la
  // ventana de apertura coach-first. Gate de las acciones que "toman la
  // iniciativa": elegir actividad (pill) y pedir ideas (Inspire).
  const isUserTurn = !gateEnded && !awaitingOpener && state === 'listening';
  // Mensaje del user en vuelo (thinking que NO es la apertura): habilita el
  // "Cancelar mi mensaje" del mic y a la vez lo bloquea. OJO: el mic permite
  // barge-in mientras el coach HABLA ('speaking'), por eso su gate NO es
  // `!isUserTurn` — se arma con estos flags (ended || awaitingOpener || turnInFlight).
  const turnInFlight =
    !gateEnded && !awaitingOpener && replyFailure == null && state === 'thinking';
  // Gate del hold del composer (el mic permite barge-in en 'speaking', por eso
  // no usa `isUserTurn`). El texto nunca se bloquea — `sendComposerText` sólo
  // gatea el cierre server-side.
  const holdDisabled = gateEnded || awaitingOpener || turnInFlight;

  // ── Game mode arena (plan 47): stamps, score, challenge, game controls ──
  const sendGameAction = useCallback((action: GameActionRequest): void => {
    sessionRef.current?.sendGameAction(action);
  }, []);
  const pauseSession = useCallback((): void => session.pauseSession(), [session]);
  const resumeSession = useCallback((): void => session.resumeSession(), [session]);
  const gamePractice = useMemo<GamePracticeHost>(
    () => ({
      live: connectionStatus === 'open' && !gateEnded,
      paused: session.paused,
      pause: pauseSession,
      resume: resumeSession,
    }),
    [connectionStatus, gateEnded, session.paused, pauseSession, resumeSession],
  );
  const gameArena = useGameArena({
    messages,
    activeSeed: seedMenu.activeSeed,
    gameRound: session.gameRound,
    coachBusy: state === 'thinking' || state === 'speaking' || awaitingOpener,
    ended: gateEnded,
    sendGameAction,
    gameSolutions: session.gameSolutions,
    liveCoach: showLiveCoach,
    historySolutions: historyGameSolutions,
    sessionId: session.sessionId,
  });

  // ── Hilo vacío y mudo (reporte de campo 2026-07-28) ─────────────────────
  // Estados donde el área de mensajes no renderiza NADA — ni burbujas, ni
  // "pensando", ni error, ni banner de conexión: la pantalla quedaba negra
  // sin feedback. Dos huecos reales:
  //  - `!historyReady`: "Continuar"/reattach todavía trayendo el transcript
  //    previo (el start() está gateado por eso) → 'loading'.
  //  - reattach con 0 turnos sembrados (F5 antes de que el opener persista,
  //    o el fetch del transcript falló): el coach NO re-saluda — sin señal,
  //    el hilo quedaba negro esperando que el user hable → 'your-turn'.
  // Con arranque fallido / conexión caída / cierre server-side mandan sus
  // propias superficies (card roja / ConnectionBanner / SessionEndedCard). El
  // aviso de mic caído NO cuenta: esa charla está viva y sigue necesitando el
  // resto del feedback del hilo.
  const threadMute =
    messages.length === 0 &&
    !showThinking &&
    !showLiveCoach &&
    !startError &&
    !gateEnded &&
    connectionHealthy;
  const programIntro = useProgramIntro(programIntroEligible, trackers.subUnit);

  const emptyThreadHint: 'loading' | 'your-turn' | null = !threadMute
    ? null
    : !historyReady
      ? 'loading'
      : reattachSessionId != null && !recording
        ? 'your-turn'
        : null;

  // Estado del pipeline para la barra viva del modo charla (drenaje del player
  // incluido: mientras el audio suena, el coach sigue "hablando").
  const freeStatus: 'listening' | 'thinking' | 'speaking' =
    state === 'speaking' || session.isAudioPlaying
      ? 'speaking'
      : state === 'thinking'
        ? 'thinking'
        : 'listening';
  // «Te escucho» visual (#518): el contador de turnos del alumno le marca a la
  // barra viva el cierre del turno.
  const userTurnTick = messages.filter((m) => m.who === 'user').length;

  const leave = useCallback((): void => {
    const sid = sessionRef.current?.sessionId ?? lastSessionIdRef.current;
    const msgs = sessionRef.current?.messages ?? [];
    // `turnCount` (coach+user) sólo decide si RECORDAR la charla para el
    // "Seguir la charla" del player de Escuchar. El gate del DEBRIEF usa los
    // turnos del USER — así coincide con el copy del backend y no se abre un
    // debrief que después se disculpa por "sesión corta".
    //
    // Las burbujas históricas cuentan sólo si son de ESTA fila: tras un F5 el
    // backend reattachea la MISMA sesión (mismo `sessionId`) y sus turnos ya
    // están en el debrief, así que ignorarlos mandaba a Home una charla larga
    // por el sólo hecho de haber recargado. Ver `isSameRowHistory`.
    const sameRowHistory = isSameRowHistory(sid, reattachSessionId);
    const turnCount = countGateTurns(msgs, { sameRowHistory });
    const userTurnCount = countGateUserTurns(msgs, { sameRowHistory });
    void sessionRef.current?.stop();
    // Dirección "Escuchar" (19): la charla fue sobre un capítulo → marcamos
    // "conversado" y volvemos al player (no al debrief). El audio sigue vivo en
    // el store (mini-player), así que el player retoma en la misma posición.
    // Volvemos por HISTORIAL (pop), no con push: la conversación se abre
    // siempre desde el player, así que back lo restituye. Pushear acá apilaba
    // player→conversación→player→… y minimizar el player "volvía" a la
    // conversación — ciclo sin salida. `location.key === 'default'` = carga
    // directa sin historial in-app → replace como fallback.
    //
    // TODAS las salidas pasan por `exitHistory` (guarda del back): saca primero
    // el centinela y recién entonces navega, y las navegaciones que no popean
    // REEMPLAZAN la entrada de la conversación — una charla terminada no tiene
    // que volver a aparecer con un back (remontaría y abriría OTRA sesión).
    if (listenStart) {
      const { book, edition, keypoint } = listenStart;
      void listen.markConversed({ book, edition, keypoint });
      // Recordamos la sesión para que el CTA del player ofrezca "Seguir la
      // charla" (resumeSessionId) en vez de arrancar de cero. Sólo charlas
      // con contenido y no-incógnito: una sesión vacía u oculta no se retoma.
      if (sid && turnCount > 0 && !incognitoRef.current) {
        listen.rememberCoachSession({ book, edition, keypoint }, sid);
      }
      exitHistory(() => {
        if (location.key !== 'default') void navigate(-1);
        else void navigate('/listen/player', { replace: true });
      });
      return;
    }
    // Examen de nivelación: no hay debrief — vamos al reveal del nivel, que
    // hace polling de la estimación del distiller (calibration).
    if (examMode) {
      exitHistory(() => void navigate('/onboarding/level/result', { replace: true }));
      return;
    }
    // Charla guiada A0 (plan 33): no hay debrief (a0 es no-persistible: no mueve
    // CEFR/radar ni dispara el distiller). Salir vuelve al motor A0 en /home. El
    // boss tiene su propio flujo de cierre (graduación) — esto cubre el salir con
    // la X y la micro-charla.
    if (a0Mission) {
      exitHistory(() => void navigate('/home', { replace: true }));
      return;
    }
    // Charla privada: no hay debrief (el analyze se saltea y la sesión queda
    // oculta del historial) — volvemos directo a Home.
    if (sid && userTurnCount >= DEBRIEF_MIN_TURNS && !incognitoRef.current) {
      exitHistory(() => void navigate(`/debrief/${sid}`, { replace: true }));
    } else {
      exitHistory(() => void navigate('/home', { replace: true }));
    }
  }, [
    navigate,
    exitHistory,
    listen,
    location.key,
    listenStart,
    examMode,
    incognitoRef,
    a0Mission,
    reattachSessionId,
  ]);

  // «Armá tu nave» (Emmanuel, 20/10): volver a la nave cierra la charla igual que la X —la sesión se
  // termina en el servidor, así el resumen y las correcciones quedan— pero va al Inicio, donde está
  // la nave, en vez del resumen. Por `exitHistory`, como toda salida (un back no reabre la charla).
  const leaveToShip = useCallback((): void => {
    void sessionRef.current?.stop();
    exitHistory(() => void navigate('/home', { replace: true }));
  }, [exitHistory, navigate]);

  // ── Aviso de salida cuidada (fix "sesión demasiado corta") ────────────────
  // La X de la conversación pasa por acá en vez de llamar `leave` directo. Si la
  // charla es demasiado corta para dejar un debrief útil pero el user YA habló
  // algo, abrimos un aviso ("¿seguís o salís?") antes de mandar a Home en seco.
  // No aplica a: charla de Escuchar, examen, A0, incógnito, sesiones sin id, ni
  // cuando el user no habló nada (0 turnos → salida esperada, no molestamos) o
  // ya alcanzó el mínimo (≥3 → va derecho al debrief).
  const [shortLeavePrompt, setShortLeavePrompt] = useState(false);
  const requestLeave = useCallback((): void => {
    const sid = sessionRef.current?.sessionId ?? lastSessionIdRef.current;
    const userTurnCount = countGateUserTurns(sessionRef.current?.messages ?? [], {
      sameRowHistory: isSameRowHistory(sid, reattachSessionId),
    });
    const avoidable =
      sid != null &&
      !listenStart &&
      !examMode &&
      !a0Mission &&
      !incognitoRef.current &&
      userTurnCount >= 1 &&
      userTurnCount < DEBRIEF_MIN_TURNS;
    if (avoidable) {
      setShortLeavePrompt(true);
      return;
    }
    leave();
  }, [leave, listenStart, examMode, a0Mission, incognitoRef, reattachSessionId]);
  const confirmLeaveShort = useCallback((): void => {
    setShortLeavePrompt(false);
    leave();
  }, [leave]);
  const dismissLeaveShort = useCallback((): void => setShortLeavePrompt(false), []);

  // El back del historial (browser / gesto / botón de Android) es la X: con el
  // aviso abierto lo cierra (convención de Android: back = cerrar la hoja =
  // "seguir practicando"); si no, pide la salida cuidada. Ref (no efecto) para
  // que el handler de `popstate` lea siempre la versión del último render.
  const shortLeavePromptRef = useRef(false);
  shortLeavePromptRef.current = shortLeavePrompt;
  onBackRef.current = () => {
    if (shortLeavePromptRef.current) dismissLeaveShort();
    else requestLeave();
  };

  return {
    session,
    state,
    connectionStatus,
    reconnect,
    messages,
    transcript,
    assistantText,
    sessionId,
    historySkillCredits,
    audioRecording,
    paused: session.paused,
    settings,
    setSettings,
    setGlobalIntensity,
    correctionLevelEff,
    correctionRec,
    speedRec,
    speedEff,
    isVoiceInput,
    isVoiceOutput,
    coachMuted,
    toggleCoachMuted,
    muteNotice,
    botAudioFlags,
    micHidden,
    recording,
    recorder,
    freeChat,
    freeStatus,
    userTurnTick,
    holdDisabled,
    coach,
    inspire,
    assisted,
    assistedPaused,
    sendComposerText,
    sendInspireSuggestion,
    interrupt,
    a0Mission,
    a0SuggestionStep: a0Suggestions.step,
    sendA0Suggestion,
    a0CelebrateTick,
    drillErr,
    setDrillErr,
    settingsOpen,
    setSettingsOpen,
    incognito,
    toggleIncognito,
    scenarioMission,
    scenarioLabel,
    planLabel,
    planObjectives,
    ...trackers,
    chooseLesson: (choice) => {
      const part = trackers.lessonCompletion;
      const next = nextLessonTarget(part);
      if (choice !== 'next' || !part || !next) return;
      void sessionRef.current?.stop();
      exitHistory(() => opts.onNextLesson?.(part.unitId, next));
    },
    leaveToProgram: () => {
      void sessionRef.current?.stop();
      exitHistory(() => void navigate('/program', { replace: true }));
    },
    scenarioEndActions: {
      onDebrief: leave,
      onRepeat: () => {
        void sessionRef.current?.stop();
        exitHistory(() => opts.onRestart?.());
      },
      onOther: () => {
        void sessionRef.current?.stop();
        exitHistory(() => void navigate('/scenarios', { replace: true }));
      },
    },
    getKaraokeRenderEndIdx,
    getKaraokeCharIndex,
    pauseMainCapture,
    resumeMainCapture,
    threadRef,
    orbRef,
    threadUnpinned,
    handleThreadScroll,
    jumpToLatest,
    micIssue,
    micRetryPhase,
    requestMic,
    startError,
    showLiveCoach,
    showLiveUser,
    commitPending,
    showThinking,
    replyFailure,
    retryFailedReply,
    awaitingOpener,
    emptyThreadHint,
    programIntro,
    isUserTurn,
    turnInFlight,
    isSearchingWeb,
    examMode,
    listenActive: listenStart != null,
    serverEnded,
    serverClosing,
    resumeConversation,
    leave,
    requestLeave,
    shortLeavePrompt,
    confirmLeaveShort,
    dismissLeaveShort,
    gameArena,
    gamePractice,
    // «Armá tu nave»: el juego de esta charla para la barra de la pieza; mientras la sesión conecta,
    // el de arranque (así la barra es la de los Motores desde el primer momento, no la de la Cabina).
    shipSeed: seedMenu.activeSeed ?? (sessionId == null ? start.autoSeed : null),
    shipCanAsk: isUserTurn && !session.isAudioPlaying && !recording,
    leaveToShip,
    // Semilla de conversación (menú de temas, modo libre)
    ...seedMenu,
  };
}
